/**
 * CustomBarraAtalhos — barra fixa de atalhos no rodapé, só no celular.
 *
 * É o que dá cara de aplicativo para quem abre o app no telefone. No desktop ela
 * não existe — a navegação de lá é o painel lateral.
 *
 * A classe `adminuiux-mobile-footer` é do tema (`_footer.scss`): fixa embaixo,
 * translúcida com blur, respeitando `env(safe-area-inset-bottom)` para o notch.
 * O `InternalLayout` já media a altura dela e devolvia como `padding-bottom` no
 * <body> — a conta existia desde sempre, só faltava a barra.
 *
 * OS ATALHOS SEGUEM O PERFIL, pelo mesmo mecanismo da CustomSidebar: `/api/auth/me`
 * devolve `secoes`, e o item só aparece se o acesso estiver liberado. Isso
 * importa mais aqui do que no menu lateral: o instalador instala o app no
 * celular e é POR ESTA BARRA que ele vai lançar despesa em campo. Um atalho que
 * ele não pode abrir ocupa o lugar do único que ele realmente usa.
 *
 * Espaço é curto no celular: no máximo 4 atalhos + Menu (`MAXIMO`). Em vez de
 * exigir que se tire um item ao acrescentar outro, a LISTA É UMA FILA DE
 * PRIORIDADE e o corte é automático — quem tem acesso a mais coisas perde o fim
 * da fila, não ganha uma barra de sete ícones minúsculos. Ordenar aqui é decidir
 * o que aparece: item novo entra na posição que merece.
 *
 * Portado do projeto irmão `webapp-rm-ops` (`MobileFooter.tsx`), 09/08/2026.
 */
import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/auth/auth-context';

interface Atalho {
  to: string;
  icon: string;
  nome: string;
  /** A permissão que libera este atalho. Sem permissão = livre para quem logou. */
  permissao?: string;
  /** `end` no NavLink: `/despesas` não deve ficar ativo em `/despesas/nova`. */
  exato?: boolean;
}

/** Quantos atalhos cabem antes do Menu. Ver o cabeçalho do arquivo. */
const MAXIMO = 4;

const ATALHOS: Atalho[] = [
  { to: '/inicio', icon: 'bi-house-door', nome: 'Início' },
  // LANÇAR DESPESA vem primeiro depois do Início, de propósito: é a ação que o
  // instalador faz em campo, com a nota na mão, e as outras ele faz sentado.
  // Aponta para o formulário direto, não para a lista — quem abre o app para
  // lançar não quer passar por uma lista antes.
  // `lancar`, e não "ver despesas": o atalho é para LANÇAR, e é exatamente essa
  // a permissão que o instalador tem.
  { to: '/despesas/nova', icon: 'bi-receipt', nome: 'Despesa', permissao: 'despesas.lancamento.lancar' },
  { to: '/agenda/instalacoes', icon: 'bi-calendar-week', nome: 'Agenda', permissao: 'agenda.visitas.ver' },
  {
    to: '/ordens-servico',
    icon: 'bi-clipboard-check',
    nome: 'OS',
    permissao: 'ordens-servico.proprias.ver',
    exato: true,
  },
];

/** A pessoa alcança este atalho? */
export function atalhoLiberado(
  a: Pick<Atalho, 'permissao'>,
  pode: (p: string) => boolean,
): boolean {
  if (!a.permissao) return true;
  return pode(a.permissao);
}

const CustomBarraAtalhos: React.FC = () => {
  const { pode } = useAuth();

  // Abre o painel lateral (mesmas classes que o botão do header manipula).
  const abrirMenu = () => {
    document.body.classList.remove('sidebar-close');
    document.body.classList.add('sidebar-open');
  };

  const visiveis = ATALHOS.filter((a) => atalhoLiberado(a, pode)).slice(0, MAXIMO);

  return (
    <div className="adminuiux-mobile-footer hide-on-scrolldown d-lg-none">
      <ul className="nav nav-justified">
        {visiveis.map((a) => (
          <li className="nav-item" key={a.to}>
            <NavLink className="nav-link text-center" to={a.to} end={a.exato}>
              <i className={`bi ${a.icon}`}></i>
              <span className="nav-text d-block small">{a.nome}</span>
            </NavLink>
          </li>
        ))}
        <li className="nav-item">
          <button
            /* SEM `btn btn-link`, de propósito. Este é o único <button> da
               barra — os outros quatro são <NavLink>. Com as classes de botão
               ele começava 5px à direita do <li> que o contém e passava da tela
               em TODA página do celular (medido em 25/08: o li ia até 390, o
               botão até 395). Um botão sem cara de botão precisa só do reset:
               sem borda, sem fundo, e o `nav-link` faz o resto — igualzinho aos
               irmãos. */
            className="nav-link text-center w-100 border-0 bg-transparent"
            type="button"
            onClick={abrirMenu}
          >
            <i className="bi bi-list"></i>
            <span className="nav-text d-block small">Menu</span>
          </button>
        </li>
      </ul>
    </div>
  );
};

export default CustomBarraAtalhos;
